/* eslint-disable @angular-eslint/no-input-rename */
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  ChangeDetectionStrategy,
  Input,
  EventEmitter,
  Output,
  AfterViewInit,
} from '@angular/core';
import { Params, Router, RouterLink } from '@angular/router';
import { Subscription, Observable, Subject, map, takeUntil } from 'rxjs';
import { UserModel } from '@app/shared/models/api/user.model';
import { MetaService } from '@app/shared/services/meta.service';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { WindowUtils } from '@app/shared/utils/window.util';
import { AkitaAuthService } from '@app/akita/api/auth/state/auth.service';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { GoogleAnalyticsService } from '@app/core/services/google-analytics.service';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaAuthQuery } from '@app/akita/api/auth/state/auth.query';
import { ApiError } from '@app/shared/models/api/api-error.model';
import {
  logSignupApple,
  logSignupFacebook,
  logSignupGoogle,
  logSignupGoogleRequest,
} from '@app/core/services/google-analytics.lazy';
import { AkitaCartService } from '@app/akita/cart/state/cart.service';
import { FacebookNewIconComponent } from '../../../shared/components/icons/generated/facebook-new/facebook-new.component';
import { GoogleIconComponent } from '../../../shared/components/icons/generated/google/google.component';
import { AppleIconComponent } from '../../../shared/components/icons/generated/apple/apple.component';
// eslint-disable-next-line max-len
import { WelcomeIllustrationIconComponent } from '../../../shared/components/icons/generated/welcome-illustration/welcome-illustration.component';
import { FlatLogoIconComponent } from '../../../shared/components/icons/generated/flat-logo/flat-logo.component';
import { RemoveIconComponent } from '../../../shared/components/icons/generated/remove/remove.component';
import { NgIf, AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-auth-intro',
  templateUrl: './auth-intro.template.html',
  styleUrls: ['./auth-intro.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoModule,
    NgIf,
    RemoveIconComponent,
    FlatLogoIconComponent,
    WelcomeIllustrationIconComponent,
    AppleIconComponent,
    GoogleIconComponent,
    FacebookNewIconComponent,
    RouterLink,
    AsyncPipe,
  ],
})
export class AuthIntroComponent implements AfterViewInit, OnDestroy {
  @Input() public inDialog?: boolean | null = null;
  @Input('email') public placeholderEmail?: string | null = null;
  @Input('username') public placeholderUsername?: string | null = null;
  @Input('previous-mode') public previousMode?: string | null = null;
  @Input('next-mode') public nextMode?: string | null = null;
  @Input('success-path') public successPath?: Array<string> | null = null;
  @Input('success-params') public successRedirectQueryParams?: Params | null = null;
  @Input('skip-redirect') public skipRedirect?: boolean | null = null;

  @Output() public readonly handleRedirect: EventEmitter<void> = new EventEmitter();
  @Output() public readonly dialogModeChange: EventEmitter<any> = new EventEmitter();
  @Output() public readonly closeDialog: EventEmitter<boolean> = new EventEmitter();

  public redirect$: Observable<string>;

  public readonly signUpQueryParams$: Observable<Params>;
  public googleAuthSDKLoaded$: Observable<boolean>;
  public signingWithApple$: Observable<boolean>;
  public signingWithGoogle$: Observable<boolean>;
  public signingWithFacebook$: Observable<boolean>;

  public signWithAppleError$: Observable<ApiError | null>;
  public signWithGoogleError$: Observable<ApiError | null>;
  public signWithFacebookError$: Observable<ApiError | null>;

  public submitted: boolean;
  public failed: boolean;
  public loading: boolean;
  public error?: string | null;
  private readonly isBrowser: boolean;

  private readonly langChangesObserver: Subscription | null;
  private userLoginObserver?: Subscription | null;

  public lessThan$: Observable<ScreenSizeInfoState>;
  public isRtlLayout$: Observable<boolean>;

  private readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly router: Router,
    private readonly meta: MetaService,
    private readonly translateService: TranslocoService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly akitaAuthService: AkitaAuthService,
    private readonly akitaAuthQuery: AkitaAuthQuery,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private readonly akitaCartService: AkitaCartService
  ) {
    this.destroySubject = new Subject<void>();
    this.subscriptions = new Subscription();

    this.isBrowser = this.akitaRouterQuery.isBrowser;
    this.submitted = false;
    this.loading = false;
    this.failed = false;
    this.redirect$ = this.akitaRouterQuery.selectRedirect();

    this.lessThan$ = this.akitaScreenQuery.selectLessThan();
    this.isRtlLayout$ = this.akitaRouterQuery.selectIsRtlLayout();

    this.googleAuthSDKLoaded$ = this.akitaAuthQuery.selectAuthGoogleSDKLoaded();

    this.signingWithApple$ = this.akitaAuthQuery.selectSigningInWithApple();
    this.signingWithGoogle$ = this.akitaAuthQuery.selectSigningInWithGoogle();
    this.signingWithFacebook$ = this.akitaAuthQuery.selectSigningInWithFacebook();

    this.signWithAppleError$ = this.akitaAuthQuery.selectSignWithAppleError();
    this.signWithGoogleError$ = this.akitaAuthQuery.selectSignWithGoogleError();
    this.signWithFacebookError$ = this.akitaAuthQuery.selectSignWithFacebookError();

    this.signUpQueryParams$ = this.akitaRouterQuery.selectQueryParamsToKeep().pipe(
      map((params: Params) => ({
        ...(params || {}),
        ...this.signUpQueryParams,
      }))
    );

    this.langChangesObserver = this.translateService.langChanges$
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: () => {
          const description = this.translateService.translate('app_title');
          this.meta.setTitle('title_login', true);
          this.meta.setCanonical();
          this.meta.setAlternateHreflangsTags(null);

          this.meta.setTags([
            { name: 'og:url', content: `${WindowUtils.getLocationHref() || ''}` },
            { name: 'og:type', content: `website` },
            { name: 'og:description', content: description },
            { name: 'description', content: description },
          ]);
        },
        error: () => {},
      });

    if (this.isBrowser) {
      this.userLoginObserver = this.akitaAuthService.initializeAppleAuthAsync();
    }
  }
  public ngAfterViewInit(): void {
    this.akitaAuthService.resetSocialAuthErrors();
  }

  /**
   * Method called when the Sign in (Popsy or Facebook) completes
   * @param user The user instance from the Backend
   */
  public loggedIn(user: UserModel, skipReportingToStore?: boolean | null): boolean {
    this.loading = false;
    if (user) {
      if (!skipReportingToStore) {
        this.akitaAuthService.userSignedIn(user);
      }
    }
    this.changeDetector.markForCheck();

    if (this.akitaRouterQuery.redirect && user && user.oauth && user.oauth.token) {
      WindowUtils.setLocationHref(
        `${this.akitaRouterQuery.redirect}${user.oauth.token.accessToken}`
      );
      return false;
    } else {
      let params = {
        ...(this.successRedirectQueryParams || {}),
      };
      if (this.akitaRouterQuery.redirect) {
        params = { ...params, redirect: encodeURI(this.akitaRouterQuery.redirect) };
      }

      this.handleRedirect.next();
      if (!this.skipRedirect) {
        if (this.successPath && this.successPath.length > 0) {
          // Replace the ID in the fragment (since we did not know the ID until now)
          for (let i = 0; i < this.successPath.length; i += 1) {
            this.successPath[i] = this.successPath[i].replace(
              '--logged-user-id--',
              user.id
            );
          }

          this.router
            .navigate(this.successPath, {
              queryParams: params,
            })
            .catch((error) => {
              console.log(error);
            });
        } else {
          this.router
            .navigate(this.akitaRouterQuery.homeUrlFragment, {
              queryParams: params,
            })
            .catch((error) => {
              console.log(error);
            });
        }
      }
    }

    this.close(false);
    return true;
  }

  public onSignWithGoogle(): boolean {
    this.loading = true;
    this.submitted = true;
    this.failed = false;
    this.changeDetector.markForCheck();

    if (this.userLoginObserver) {
      this.userLoginObserver.unsubscribe();
    }

    logSignupGoogleRequest(this.googleAnalyticsService.logEventWrapper);
    // this.userLoginObserver = this.akitaAuthService.signWithGoogleAsync();
    this.userLoginObserver = this.akitaAuthService
      .signWithGoogle()
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (user: UserModel | null) => {
          logSignupGoogle(this.googleAnalyticsService.auth, user);
          this.submitted = false;
          this.subscriptions.add(this.akitaCartService.transferAnonymousCartAsync());

          if (user) {
            this.loggedIn(user, true);
          }
        },
        error: () => {
          this.setError('UNKNOWN');
          this.failed = true;
          this.submitted = false;
          this.loading = false;
          this.changeDetector.markForCheck();
        },
      });

    return false;
  }

  public onSignWithApple(): boolean {
    this.loading = true;
    this.submitted = true;
    this.failed = false;
    this.changeDetector.markForCheck();

    if (this.userLoginObserver) {
      this.userLoginObserver.unsubscribe();
    }

    this.userLoginObserver = this.akitaAuthService
      .signWithApple()
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (user: UserModel | null) => {
          logSignupApple(this.googleAnalyticsService.auth, user);
          this.submitted = false;
          this.subscriptions.add(this.akitaCartService.transferAnonymousCartAsync());
          if (user) {
            this.loggedIn(user, true);
          }
        },
        error: () => {
          this.setError('UNKNOWN');
          this.failed = true;
          this.submitted = false;
          this.loading = false;
          this.changeDetector.markForCheck();
        },
      });
    return false;
  }

  /**
   * Sign In with Facebook
   */
  public onSignInWithFacebook(): void {
    this.loading = true;
    this.submitted = true;
    this.failed = false;
    this.changeDetector.markForCheck();

    if (this.userLoginObserver) {
      this.userLoginObserver.unsubscribe();
    }

    this.userLoginObserver = this.akitaAuthService
      .signWithFacebook()
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (user: UserModel | null) => {
          logSignupFacebook(this.googleAnalyticsService.auth, user);
          this.submitted = false;
          this.subscriptions.add(this.akitaCartService.transferAnonymousCartAsync());
          if (user) {
            this.loggedIn(user, true);
          }
        },
        error: (error: unknown) => {
          console.log(error);
          this.setError('UNKNOWN');
          this.failed = true;
          this.submitted = false;
          this.loading = false;
          this.changeDetector.markForCheck();
        },
      });
  }

  private setError(error?: string | null): void {
    this.error = `${error || ''}`;
    this.changeDetector.markForCheck();
  }

  private get signUpQueryParams(): any {
    let params = {};
    const email =
      this.akitaRouterQuery.params.email || this.akitaRouterQuery.queryParams.email || '';
    if (email) {
      params = { ...params, email: email };
    }
    const username =
      this.akitaRouterQuery.params.username ||
      this.akitaRouterQuery.queryParams.username ||
      '';
    if (username) {
      params = { ...params, username: username };
    }
    if (this.akitaRouterQuery.redirect) {
      params = { ...params, redirect: encodeURI(this.akitaRouterQuery.redirect) };
    }
    return params;
  }

  public authUrlFragment(authType: string): Array<string> {
    const fragment = this.akitaRouterQuery.userUrlFragment;
    fragment.push(authType || 'sign-up');
    return fragment;
  }

  public changeDialogMode(mode?: string | null): boolean {
    const username: string | null =
      this.akitaRouterQuery.params.username ||
      this.akitaRouterQuery.queryParams.username ||
      this.placeholderUsername ||
      null;
    const email: string | null =
      this.akitaRouterQuery.params.email ||
      this.akitaRouterQuery.queryParams.email ||
      this.placeholderEmail ||
      null;

    this.dialogModeChange.emit({
      mode: mode || 'auth-intro',
      email: email,
      username: username,
      previousMode: 'auth-intro',
    });

    return false;
  }

  public close(dismiss?: boolean): boolean {
    this.closeDialog.emit(dismiss);
    return false;
  }

  public stopPropagation(): boolean {
    return false;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.userLoginObserver) {
      this.userLoginObserver.unsubscribe();
    }

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }

    if (this.langChangesObserver) {
      this.langChangesObserver.unsubscribe();
    }
  }
}
